import { create } from 'zustand';
import { GroupPermission } from '../../../types/admin/GroupPermission';
import { PatchOperation } from '../../../types/requests/PatchOperation';
import { PostResponseData } from '../../../types/response/PostResponseData';
import { ResponseData } from '../../../types/response/ResponseData';
import ResponseError from '../../../types/response/ResponseError';
import { User } from '../../../types/users/User';
import { UserGroup } from '../../../types/UserGroup';

export interface GroupsStore {
    groupSearchResults: ResponseData<UserGroup[]>;
    groupListUsersResponse: ResponseData<User[]>;
    group: UserGroup;
    isSearching: boolean;
    isLoadingOrganization: boolean;
    error?: ResponseError;
    phrase?: string;
    organization?: string;
    availablePermissions?: Record<string, string[]>;
    groupPermissions: GroupPermission[] | undefined;
    groupCreatedResponse: PostResponseData;
    groupCreateName?: string | undefined;
    groupWasCreated: boolean;
    groupId: string | undefined;
    fetchedUsersInGroup: boolean;
    groupAddUserToResponse: PostResponseData;
    userId: string;
    isGroupCreating: boolean;
    searchError?: ResponseError;
    createError?: ResponseError;
    groupSearchStarted: (phrase: string) => void;
    groupSearchSucceeded: (results: ResponseData<UserGroup[]>) => void;
    groupSearchFailed: (error: ResponseError) => void;
    getGroupDetailsStarted: () => void;
    getGroupDetailsSucceeded: (group: UserGroup) => void;
    getGroupDetailsFailed: () => void;
    getOrganizationPermissionsStarted: () => void;
    getOrganizationPermissionsSucceeded: (permissions: Record<string, string[]>) => void;
    getOrganizationPermissionsFailed: () => void;
    clearOrganizationPermissions: () => void;
    groupPermissionsToggleStarted: (payload: PatchOperation[]) => void;
    groupPermissionsToggleSucceeded: (response: ResponseData<UserGroup>) => void;
    patchGroupFailed: (error: ResponseError) => void;
    groupStoreAdded: (store: string) => void;
    groupStoreRemoved: (store: string) => void;
    deleteGroupStarted: () => void;
    deleteGroupSucceeded: (groupId: string) => void;
    deleteGroupFailed: (error: ResponseError) => void;
    createGroupStarted: (name: string) => void;
    createGroupSucceeded: (response: PostResponseData) => void;
    createGroupFailed: (error: ResponseError) => void;
    resetCreateGroup: () => void;
    getGroupListUsersStarted: (group: { organization: string; groupId: string }) => void;
    getGroupListUsersSucceeded: (groupListUsersResponse: ResponseData<User[]>) => void;
    getGroupListUsersFailed: (error: ResponseError) => void;
    addUserToGroupStarted: (data: {
        groupId: string;
        userId: string;
        organizationId: string;
    }) => void;
    addUserToGroupSucceeded: (data: { response: PostResponseData; user: User }) => void;
    addUserToGroupFailed: (error: ResponseError) => void;
    removeUserFromGroupStarted: (data: {
        groupId: string;
        userId: string;
        organizationId: string;
    }) => void;
    removeUserFromGroupSucceeded: () => void;
    removeUserFromGroupFailed: (error: ResponseError) => void;
    reset: () => void;
}

export const initialState = {
    groupSearchResults: {} as ResponseData<UserGroup[]>,
    groupListUsersResponse: {} as ResponseData<User[]>,
    group: {} as UserGroup,
    availablePermissions: {} as Record<string, string[]>,
    groupPermissions: undefined,
    isSearching: false,
    isLoadingOrganization: false,
    organization: '',
    error: undefined,
    groupCreatedResponse: {} as PostResponseData,
    groupCreateName: undefined,
    groupWasCreated: false,
    groupId: undefined,
    isGroupCreating: false,
    fetchedUsersInGroup: false,
    groupAddUserToResponse: {} as PostResponseData,
    userId: '',
    searchError: undefined,
    createError: undefined,
};

export const useGroupsStore = create<GroupsStore>((set, get) => ({
    ...initialState,

    groupSearchStarted: (phrase: string) =>
        set({
            isSearching: true,
            phrase: phrase,
            searchError: undefined,
        }),

    groupSearchSucceeded: (results: ResponseData<UserGroup[]>) =>
        set({ isSearching: false, groupSearchResults: results }),

    groupSearchFailed: (error: ResponseError) =>
        set({
            isSearching: false,
            groupSearchResults: {} as ResponseData<UserGroup[]>,
            searchError: error,
        }),

    getGroupDetailsStarted: () =>
        set({ isSearching: true, error: undefined, groupPermissions: undefined }),

    getGroupDetailsSucceeded: (group: UserGroup) =>
        set({ isSearching: false, group: group, error: undefined }),

    getGroupDetailsFailed: () => set({ isSearching: false, group: {} as UserGroup }),

    getOrganizationPermissionsStarted: () => set({ isSearching: true }),

    getOrganizationPermissionsSucceeded: (permissions: Record<string, string[]>) =>
        set({ isSearching: false, availablePermissions: permissions }),

    getOrganizationPermissionsFailed: () =>
        set({ isSearching: false, availablePermissions: {} as Record<string, string[]> }),

    clearOrganizationPermissions: () =>
        set({ availablePermissions: {} as Record<string, string[]>, group: {} as UserGroup }),

    groupPermissionsToggleStarted: (payload: PatchOperation[]) =>
        set({
            group: {
                ...get().group,
                isModifyingPermissions: true,
                payload: payload,
            },
        }),

    groupPermissionsToggleSucceeded: (response: ResponseData<UserGroup>) =>
        set({
            group: {
                ...get().group,
                isModifyingPermissions: false,
                permissions: response.data.permissions,
            },
            error: undefined,
        }),

    patchGroupFailed: (error: ResponseError) =>
        set({
            error: error,
            group: {
                ...get().group,
                isModifyingPermissions: false,
            },
        }),

    groupStoreAdded: (store: string) =>
        set({
            group: {
                ...get().group,
                connectedStores: [...get().group.connectedStores, store],
            },
            error: undefined,
        }),

    groupStoreRemoved: (store: string) =>
        set({
            group: {
                ...get().group,
                connectedStores: get().group.connectedStores.filter(item => item !== store),
            },
            error: undefined,
        }),

    deleteGroupStarted: () => set({ isSearching: true }),

    deleteGroupSucceeded: (groupId: string) =>
        set({
            isSearching: false,
            groupSearchResults: {
                ...get().groupSearchResults,
                data: get().groupSearchResults.data.filter(group => group.id !== groupId),
            } as ResponseData<UserGroup[]>,
            error: undefined,
        }),

    deleteGroupFailed: (error: ResponseError) => set({ isSearching: false, error: error }),

    createGroupStarted: (name: string) => set({ groupCreateName: name, isGroupCreating: true }),

    createGroupSucceeded: (response: PostResponseData) =>
        set({
            groupCreatedResponse: response,
            groupWasCreated: true,
            isGroupCreating: false,
        }),

    createGroupFailed: (error: ResponseError) =>
        set({ createError: error, isGroupCreating: false }),

    resetCreateGroup: () =>
        set({ isGroupCreating: false, groupWasCreated: false, createError: undefined }),

    getGroupListUsersStarted: (group: { organization: string; groupId: string }) =>
        set({
            organization: group.organization,
            groupId: group.groupId,
            fetchedUsersInGroup: false,
            error: undefined,
        }),

    getGroupListUsersSucceeded: (groupListUsersResponse: ResponseData<User[]>) =>
        set({ groupListUsersResponse: groupListUsersResponse, fetchedUsersInGroup: true }),

    getGroupListUsersFailed: (error: ResponseError) =>
        set({ error: error, fetchedUsersInGroup: false }),

    addUserToGroupStarted: (data: { groupId: string; userId: string; organizationId: string }) =>
        set({
            isSearching: true,
            groupId: data.groupId,
            userId: data.userId,
            organization: data.organizationId,
        }),

    addUserToGroupSucceeded: (data: { response: PostResponseData; user: User }) =>
        set({
            isSearching: false,
            groupAddUserToResponse: data.response,
            groupListUsersResponse: {
                ...get().groupListUsersResponse,
                data: [...(get().groupListUsersResponse.data ?? []), data.user],
            },
        }),

    addUserToGroupFailed: (error: ResponseError) => set({ isSearching: false, error: error }),

    removeUserFromGroupStarted: (data: {
        groupId: string;
        userId: string;
        organizationId: string;
    }) =>
        set({
            groupId: data.groupId,
            userId: data.userId,
            organization: data.organizationId,
            isSearching: true,
        }),

    removeUserFromGroupSucceeded: () =>
        set({
            groupListUsersResponse: {
                data: [
                    ...get().groupListUsersResponse.data.filter(
                        (usr: User) => usr.id !== get().userId
                    ),
                ],
                metaData: get().groupListUsersResponse.metaData,
                links: get().groupListUsersResponse.links,
            },
            isSearching: false,
        }),

    removeUserFromGroupFailed: (error: ResponseError) => set({ error: error, isSearching: false }),

    reset: () => set(initialState),
}));
